import {
  ReviewForgeElementType,
  StaticElementType,
  SubmittableElementType,
  ThankYouUpsellElementType,
} from "@redotech/redo-model/forge/review-forge-types";
import { memo } from "react";
import { Flex } from "../flex";
import { withEditor } from "../form-builder/widget-editors/widget-editor-decorator";
import { WidgetElementEditor } from "../form-builder/widget-editors/widget-element-editor";
import { THANK_YOU_UPSELL_PAGE } from "./review-form-editor-contexts";
import { ReviewFormElementMenuBar } from "./review-form-menu-bar";
import {
  AllReviewFormElements,
  elementIsWriteable,
  PageForm,
  PossiblePageElement,
  PossibleThankYouPageElement,
  ReviewElementEditorProps,
  ThankYouPageForm,
  WriteableReviewElementEditorProps,
} from "./review-form-types";
import { RichTextEditor } from "./widget-editors/rich-text-editor";
import { ReviewsDiscount } from "./widgets/review-discount";
import { ReviewsLogo } from "./widgets/review-logo";
import { ReviewMediaUpload } from "./widgets/review-media-upload";
import { ReviewsParagraph } from "./widgets/review-paragraph";
import { ReviewsProductDetails } from "./widgets/review-product-details";
import { RichText } from "./widgets/review-rich-text";
import { ReviewsShortAnswer } from "./widgets/review-short-answer";
import { ReviewSingleSelect } from "./widgets/review-single-select";
import { ReviewsStars } from "./widgets/review-stars";

const staticElementEditors: Record<
  StaticElementType,
  React.FC<ReviewElementEditorProps<any>>
> = {
  [ReviewForgeElementType.RICH_TEXT]: withEditor(RichTextEditor, RichText),
  [ReviewForgeElementType.LOGO]: withEditor(WidgetElementEditor, ReviewsLogo),
  [ReviewForgeElementType.PRODUCT_DETAILS]: withEditor(
    WidgetElementEditor,
    ReviewsProductDetails,
  ),
};

const thankYouPageElementEditors: Record<
  ThankYouUpsellElementType,
  React.FC<ReviewElementEditorProps<any>>
> = {
  ...staticElementEditors,
  [ReviewForgeElementType.DISCOUNT]: withEditor(
    WidgetElementEditor,
    ReviewsDiscount,
  ),
};

const submittableElementEditors: Record<
  SubmittableElementType,
  React.FC<WriteableReviewElementEditorProps<any, any>>
> = {
  [ReviewForgeElementType.SHORT_ANSWER]: withEditor(
    WidgetElementEditor,
    ReviewsShortAnswer,
  ),
  [ReviewForgeElementType.PARAGRAPH]: withEditor(
    WidgetElementEditor,
    ReviewsParagraph,
  ),
  [ReviewForgeElementType.STARS]: withEditor(WidgetElementEditor, ReviewsStars),
  [ReviewForgeElementType.MEDIA_UPLOAD]: withEditor(
    WidgetElementEditor,
    ReviewMediaUpload,
  ),
  [ReviewForgeElementType.SINGLE_SELECT]: withEditor(
    WidgetElementEditor,
    ReviewSingleSelect,
  ),
};

export const ReviewFormPageElement = memo(function ReviewFormPageElement({
  props,
  elementIndex,
  isSelected,
  selectElement,
}: {
  props:
    | {
        element: PossibleThankYouPageElement;
        pageIndex: THANK_YOU_UPSELL_PAGE;
        page: ThankYouPageForm;
      }
    | {
        element: PossiblePageElement;
        pageIndex: number;
        page: PageForm;
      };
  elementIndex: number;
  isSelected: boolean;
  selectElement(): void;
}) {
  const EditorComponent =
    props.pageIndex === THANK_YOU_UPSELL_PAGE
      ? thankYouPageElementEditors[props.element.type]
      : elementIsWriteable(props.element)
        ? submittableElementEditors[props.element.type]
        : staticElementEditors[props.element.type];

  function setElement(element: AllReviewFormElements) {
    const newElementsArray = props.page.value.elements.map((e, i) => {
      if (i === elementIndex) {
        return element;
      }
      return e;
    });
    props.page.setValue({ elements: newElementsArray as any });
  }

  const maxElementIndex = props.page.value.elements.length - 1;

  return (
    <Flex dir="column" position="relative">
      <EditorComponent
        index={elementIndex}
        onSelectElement={selectElement}
        selected={isSelected}
        setElement={setElement}
        widget={{
          element: props.element,
          setData: () => {},
          showRequired: false,
          data: undefined,
        }}
      />
      {isSelected && (
        <ReviewFormElementMenuBar
          element={props.element}
          elementIndex={elementIndex}
          maxElementIndex={maxElementIndex}
          pageIndex={props.pageIndex}
        />
      )}
    </Flex>
  );
});
